import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../Components/Request';
import { useAuth } from '../../../Components/AuthContext';
import hasPermission from '../../../Hooks/hasPermission';

export interface PermissionNode {
  id: string;
  name: string;
  description?: string | null;
  father_id?: string | null;
}
export interface RolePermissions {
  role_id: string;
  role_name: string;
  permissions: PermissionNode[];
}

export const useUserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  const { user: authUser } = useAuth();
  const perms = useMemo(() => {
    const p = authUser?.permissionsActives;
    return {
      canEditUsers: hasPermission(p, 'US_EDIT'),
      canViewUserAdmin: hasPermission(p, 'US_AD_VIEW'),
      canEditUsersAdmin: hasPermission(p, 'US_AD_EDIT'),
      canViewClients: hasPermission(p, 'CL_VIEW'),
      canEditClients: hasPermission(p, 'CL_EDIT'),
      canViewDeliveries: hasPermission(p, 'DL_VIEW'),
    };
  }, [authUser]);

  const [user, setUser] = useState<any>(null);
  const [deliveries, setDeliveries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);

  const [roles, setRoles] = useState<RolePermissions[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState<string>('');
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);

  const [clientsList, setClientsList] = useState<{ id: string; name: string }[]>([]);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(`/users/${id}`);
      const u = res.data.user;
      setUser(u);
      setDeliveries(res.data.delivery || []);
      setName(u.name || '');
      setEmail(u.email || '');
      setRoleId(u.role?.id || '');
      setSelectedPerms((u.permissions || []).map((p: any) => p.id));
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 401) navRef.current('/login');
      else if (status === 404) navRef.current('/users');
      else if (status === 403) navRef.current('/');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!perms.canViewUserAdmin) return;
    axiosInstance.get('/permissions-list').then((r) => setRoles(r.data || [])).catch(() => {});
  }, [perms.canViewUserAdmin]);

  useEffect(() => {
    if (!perms.canViewClients) return;
    axiosInstance.get('/clients').then((r) => setClientsList(r.data || [])).catch(() => {});
  }, [perms.canViewClients]);

  // Universo de permisos del rol seleccionado
  const optionPermissions = useMemo<PermissionNode[]>(
    () => roles.find((r) => r.role_id === roleId)?.permissions || [],
    [roles, roleId]
  );

  const changeRole = (newRole: string) => {
    setRoleId(newRole);
    const universe = new Set((roles.find((r) => r.role_id === newRole)?.permissions || []).map((p) => p.id));
    setSelectedPerms((prev) => prev.filter((pid) => universe.has(pid)));
  };

  const togglePermissions = (ids: string[]) => setSelectedPerms(ids);

  // ===== Acciones =====
  const update = useCallback(async () => {
    if (!id || !perms.canEditUsers) return;
    setSavingField('update');
    try {
      await axiosInstance.put(`/users/${id}`, {
        role_id: roleId,
        permissions: selectedPerms,
        name,
        email,
      });
      await fetchUser();
      return true;
    } catch (err: any) {
      return err?.response?.data?.message || 'Failed to update user';
    } finally {
      setSavingField(null);
    }
  }, [id, perms.canEditUsers, roleId, selectedPerms, name, email, fetchUser]);

  const toggleStatus = useCallback(async () => {
    if (!id || !perms.canEditUsers) return;
    setSavingField('status');
    try {
      await axiosInstance.put(`/user-status/${id}`, {
        status_id: user?.statusUser?.id === 1 ? 2 : 1,
      });
      await fetchUser();
    } finally {
      setSavingField(null);
    }
  }, [id, perms.canEditUsers, user, fetchUser]);

  const changePassword = useCallback(
    async (newPassword: string) => {
      if (!id) return 'No user';
      try {
        await axiosInstance.put(`/users/${id}`, { password: newPassword });
        return true;
      } catch (err: any) {
        return err?.response?.data?.message || 'Failed to change password';
      }
    },
    [id]
  );

  const assignClients = useCallback(
    async (clientIds: string[]) => {
      if (!id || clientIds.length === 0) return;
      setSavingField('client-assign');
      try {
        for (const cid of clientIds) {
          await axiosInstance.post(`/clients-reference/${id}`, { action: 'add', client_id: cid });
        }
        await fetchUser();
      } finally {
        setSavingField(null);
      }
    },
    [id, fetchUser]
  );
  const unassignClient = useCallback(
    async (clientId: string) => {
      if (!id) return;
      setSavingField(`client-remove-${clientId}`);
      try {
        await axiosInstance.post(`/clients-reference/${id}`, { action: 'remove', client_id: clientId });
        await fetchUser();
      } finally {
        setSavingField(null);
      }
    },
    [id, fetchUser]
  );

  return {
    perms,
    user,
    deliveries,
    loading,
    savingField,
    roles,
    clientsList,
    name,
    setName,
    email,
    setEmail,
    roleId,
    changeRole,
    selectedPerms,
    togglePermissions,
    optionPermissions,
    update,
    toggleStatus,
    changePassword,
    assignClients,
    unassignClient,
  };
};
